import React from 'react';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Sun,
  Moon,
  LogOut,
  Image as ImageIcon,
  BookOpen,
  GraduationCap,
  Video,
  Shield,
  Upload
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import ConsultantDocumentUpload from '../../components/workday-client/ConsultantDocumentUpload';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';
import { useState, useEffect } from 'react';

export default function ConsultantDocuments() {
  const { signOut } = useAuth();
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'onboarding' | 'training' | 'policies'>('onboarding');

  useEffect(() => {
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      setLogoLoading(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'logo_url')
          .single();

        if (error) {
          console.warn('Could not fetch logo from settings:', error.message);
          setLogoLoading(false);
          return;
        }

        if (data?.value) {
          setLogoUrl(data.value);
        }
      } catch (err) {
        console.warn('Error in logo fetch:', err);
      } finally {
        setLogoLoading(false);
      }
    } catch (error) {
      console.error('Error in fetchLogo:', error);
      setLogoLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/customer/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark' ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      <WorkdayClientHeader />

      <main className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => navigate('/workday-client/dashboard')}
              className={`flex items-center space-x-2 transition-colors ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
          </div>

          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">Consultant Documents & Resources</h1>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Share onboarding materials, training resources, and company policies with your consultants
            </p>
          </div>

          <div className="mb-8 flex flex-wrap gap-4">
            <button
              onClick={() => setActiveTab('onboarding')}
              className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                activeTab === 'onboarding'
                  ? 'bg-[#40E0D0] text-black'
                  : theme === 'dark'
                  ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <GraduationCap className="w-5 h-5 mr-2" />
              Onboarding
            </button>
            <button
              onClick={() => setActiveTab('training')}
              className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                activeTab === 'training'
                  ? 'bg-[#40E0D0] text-black'
                  : theme === 'dark'
                  ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <Video className="w-5 h-5 mr-2" />
              Training & Resources
            </button>
            <button
              onClick={() => setActiveTab('policies')}
              className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                activeTab === 'policies'
                  ? 'bg-[#40E0D0] text-black'
                  : theme === 'dark'
                  ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <Shield className="w-5 h-5 mr-2" />
              Policies
            </button>
          </div>

          <div className={`mb-8 p-6 rounded-xl ${
            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-[#40E0D0]/10">
                <Upload className="w-6 h-6 text-[#40E0D0]" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">Upload Documents for {activeTab === 'onboarding' ? 'Onboarding' : activeTab === 'training' ? 'Training & Resources' : 'Policies'}</h2>
                <p className={`mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {activeTab === 'onboarding' && 'Share onboarding guides, checklists, and welcome materials with new consultants'}
                  {activeTab === 'training' && 'Upload training videos, best practice guides, and project templates'}
                  {activeTab === 'policies' && 'Provide company policies, security guidelines, and compliance documents'}
                </p>
              </div>
            </div>
          </div>

          <ConsultantDocumentUpload category={activeTab} />

          <div className={`mt-12 p-6 rounded-xl text-center ${
            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            <BookOpen className="w-12 h-12 text-[#40E0D0] mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Document Management Best Practices</h2>
            <p className={`mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Keep your consultant resources organized and up-to-date for the best onboarding experience
            </p>
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 text-left ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              <div>
                <h3 className="font-semibold mb-2 text-[#40E0D0]">Onboarding</h3>
                <p className="text-sm">Welcome guides, checklists, and initial setup instructions for new consultants</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2 text-[#40E0D0]">Training</h3>
                <p className="text-sm">Workday best practices, video tutorials, and project-specific documentation</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2 text-[#40E0D0]">Policies</h3>
                <p className="text-sm">Security policies, code of conduct, and compliance requirements</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
